import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LINKS = [
  { key: "story", href: "#manifesto" },
  { key: "divisions", href: "#divisions" },
  { key: "quality", href: "#specs" },
  { key: "contact", href: "#contact" },
];

export default function Navbar() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#F4F1EB]/80 backdrop-blur-xl border-b border-[#3A4536]/10"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-2.5"
        >
          <Leaf strokeWidth={1.5} className="h-6 w-6 text-[#B85D43]" />
          <span className="font-serif-display text-2xl font-bold tracking-tight text-[#B85D43]">
            Herb Labs
          </span>
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.key}`}
              onClick={() => scrollTo(l.href)}
              className="link-underline overline text-[#3A4536] transition-colors hover:text-[#1A2118]"
            >
              {t(`nav.${l.key}`)}
            </button>
          ))}
          <LanguageSwitcher scrolled={scrolled} />
          <button
            data-testid="nav-cta"
            onClick={() => scrollTo("#contact")}
            className="group flex items-center gap-2 rounded-full bg-[#B85D43] px-6 py-2.5 text-sm text-[#F4F1EB] transition-colors hover:bg-[#1A2118]"
          >
            {t("nav.quote")}
          </button>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <LanguageSwitcher scrolled={scrolled} />
          <button
            data-testid="mobile-menu-toggle"
            className="text-[#1A2118]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-[#3A4536]/10 bg-[#F4F1EB]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="py-3 text-left font-serif-display text-2xl text-[#1A2118]"
                >
                  {t(`nav.${l.key}`)}
                </button>
              ))}
              <button
                onClick={() => scrollTo("#contact")}
                className="mt-2 rounded-full bg-[#B85D43] px-6 py-3 text-sm text-[#F4F1EB]"
              >
                {t("nav.quote")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
